import axios from 'axios'
import { isEmpty } from 'lodash';

import {
  KEY_IS_LOGIN_WITH_AZURE,
  KEY_ME,
  KEY_TOKEN,
  NO_TOKEN_BEARER_NEEDED_ENDPOINT
} from '@/fixtures/Constants'
import router from '@/router'
import store from '@/store'

const instance = axios.create({
  baseURL: `${process.env.VUE_APP_BASE_URL}`,
  timeout: 40000
})

const retryProcess = (originalRequest) => instance({
  ...originalRequest,
  headers: {
    Authorization: `Bearer ${store.getters['Auth/token']}`
  }
})
let isRefreshing = false
let refreshPromise = null
let loginAzurePromise = null
let isLoginRefreshing = false

const logout = () => {
  const encryptedTokenKey = btoa(KEY_TOKEN)
  const encryptedMeKey = btoa(KEY_ME)
  const encryptedIsLoginWithAzure = btoa(KEY_IS_LOGIN_WITH_AZURE)
  store.dispatch('Auth/logout')
    .then(() => {
      if (router.currentRoute.name !== 'Login') {
        router.push({ name: 'Login', replace: true })
      }
    })
    .catch(() => {
      if (router.currentRoute.name !== 'Login') {
        router.push({ name: 'Login', replace: true })
      }
    }).finally(() => {
      localStorage.removeItem(encryptedTokenKey)
      localStorage.removeItem(encryptedMeKey)
      localStorage.removeItem(encryptedIsLoginWithAzure)
    })
}
const reloginAzure = async () => {
  try {
    await store.dispatch('OidcAuth/silentRenew')
    const accessToken = store.getters['OidcAuth/accessToken'];
    await store.dispatch('Auth/loginWithAzure', { access_token: accessToken })
  } catch({ error }) {
    if(isEmpty(error) || error === 'login_required') {
      logout()
      return
    }
    return Promise.reject(error)
  }
}


const refresh = async (originalRequest) => {
  try {
    if(isRefreshing) {
      await refreshPromise
    } else {
      isRefreshing = true
      refreshPromise = store.dispatch('Auth/refreshToken');
      await refreshPromise
      isRefreshing = false
      refreshPromise = null
    }
    return retryProcess(originalRequest)
  } catch(err) {
    originalRequest._retry = false
    isRefreshing = false
    refreshPromise = null
    if (!isEmpty(err)) {
      const isLoginWithAzure = store.getters['Auth/isLoginWithAzure'];
      if(isLoginWithAzure) {
        if(loginAzurePromise) {
          await loginAzurePromise
        } else {
          isLoginRefreshing = true
          loginAzurePromise = reloginAzure()
          await loginAzurePromise
          isLoginRefreshing = false
          loginAzurePromise = null
        }
        return retryProcess(originalRequest)
      } else if(err?.error?.code === 40111) {
        logout()
        return
      } else {
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
}

instance.interceptors.request.use(req => {
  if (!NO_TOKEN_BEARER_NEEDED_ENDPOINT.some(endpoint => req.url.includes(endpoint))) {
    req.headers['Authorization'] = `Bearer ${store.getters['Auth/token']}`
  }
  return req
});

instance.interceptors.response.use(
  response => response,
  error => {
    // Safari can't get the OK response and shows 'Network Error' instead
    const safariUnrealError = error.code === undefined && error.message === 'Network Error'
    const nonSafariUnauthorized = error.response && error.response.status === 401
    // Safari don't have response on error 4xx
    const safariUnauthorized = !error.response
    const refreshRequest = error.config.url.endsWith('/auth/token')
    const loginRequest = NO_TOKEN_BEARER_NEEDED_ENDPOINT.some(endpoint => error.config.url.endsWith(endpoint))
    const internalServerError = error.response?.status === 500
    const badRequestError = error.response?.status === 400
    const originalRequest = error.config
    if (loginRequest || originalRequest._retry) {
      return Promise.reject(error.response || error)
    } else if (safariUnrealError) {
      return Promise.resolve()
    } else if ((nonSafariUnauthorized || safariUnauthorized) && !refreshRequest) {
      originalRequest._retry = true
      return refresh(originalRequest)
    } else if (internalServerError) {
      return Promise.reject(error.response || error)
    } else if (badRequestError) {
      return Promise.reject({ message: error.response.data })
    }

    return Promise.reject(error.response || error)
  }
)

export default instance
